import dayjs from "dayjs"
import React from "react"
import { useQuery } from "urql"

import { Button } from "../../components/shared/Buttons"
import EventTypeCard from "../../components/shared/EventTypeCard"
import { Flyout } from "../../components/shared/Layout"
import LoadingSpinner from "../../components/shared/LoadingSpinner"

import { GET_SCHEDULED_EVENTS_QUERY } from "./queries"

const ScheduledEventsFlyout = ({ visible, closeFlyout, eventType, onEditClick }) => {
  const [{ data, fetching, error }] = useQuery({
    query: GET_SCHEDULED_EVENTS_QUERY,
    variables: { bookableEventTypeId: eventType?.id },
    pause: !visible || !eventType
  })

  const displayEndForOccurrence = (event) => {
    const start = dayjs(event.startsAt)
    if (!eventType?.duration) return dayjs(event.endsAt)
    if (eventType.durationUnit === "day") return dayjs(event.endsAt)
    if (eventType.durationUnit === "hour") return start.add(eventType.duration, "hour")
    return start.add(eventType.duration, "minute")
  }

  const formatEventTime = (event) => {
    if (!event) return ""

    const startDate = dayjs(event.startsAt)
    const endDate = displayEndForOccurrence(event)
    const isMultiDay = eventType?.durationUnit === "day"

    if (isMultiDay) {
      // Format for multi-day events (showing start and end dates)
      const formattedStartDate = startDate.format("ddd, MMM D, YYYY")
      const formattedEndDate = endDate.format("ddd, MMM D, YYYY")
      const formattedTime = startDate.format("h:mma").toLowerCase()

      return (
        <div className="flex flex-col">
          <div className="flex items-start">
            <div className="mr-3 flex flex-col items-center py-2">
              <div className="size-2 shrink-0 rounded-full border border-gray bg-gray"></div>
              <div className="mx-auto h-[60px] w-0.5 border-l-2 border-dashed border-gray"></div>
              <div className="size-2 shrink-0 rounded-full border border-gray bg-gray"></div>
            </div>
            <div className="flex flex-col">
              <div className="font-bold">{formattedStartDate}</div>
              <div className="text-sm text-gray-dark">{formattedTime}</div>
              <div className="h-6"></div>
              <div className="font-bold">{formattedEndDate}</div>
              <div className="text-sm text-gray-dark">{formattedTime}</div>
            </div>
          </div>
        </div>
      )
    } else {
      // Format for single-day events (original implementation)
      const formattedDate = startDate.format("ddd, MMM D, YYYY")
      const formattedStartTime = startDate.format("h:mm a").toLowerCase()
      const formattedEndTime = endDate.format("h:mm a").toLowerCase()

      return (
        <div>
          <div className="font-bold">{formattedDate}</div>
          <div className="text-sm text-gray-dark">
            {formattedStartTime} - {formattedEndTime}
          </div>
        </div>
      )
    }
  }

  const renderContent = () => {
    if (fetching) return <LoadingSpinner />

    if (error) return <div className="text-red">Error loading events: {error.message}</div>

    if (!data?.bookableEventType?.bookableEvents?.length) {
      return <div className="py-8 text-center">No scheduled events yet.</div>
    }

    return (
      <div>
        {eventType ? (
          <EventTypeCard eventType={eventType} onEditClick={onEditClick} className="mb-6" />
        ) : (
          <div className="mb-6 flex items-center justify-center rounded-lg border border-gray-light p-4">
            <span className="text-gray-dark">No event type selected</span>
          </div>
        )}

        <div className="mb-4 flex items-center justify-between">
          <div className="text-lg font-bold">{data.bookableEventType.bookableEvents.length} Scheduled</div>
          <Button
            onClick={() =>
              (window.location.href = `/portal/calendar?bookableEventTypeId=${eventType?.id}#create-group-event`)
            }>
            Add New
          </Button>
        </div>

        <div className="space-y-4">
          {data.bookableEventType.bookableEvents.map((event) => (
            <div key={event.id} className={`border-b border-gray-light pb-4 first:border-t first:pt-4`}>
              <div className="flex items-center">
                <div className="grow">{formatEventTime(event)}</div>
                <div className="flex gap-8">
                  <div className="flex flex-col items-start text-sm text-gray-dark">
                    <div>
                      {event.clientsCount}/{event.capacity || "∞"}
                    </div>
                    <div>Registrants</div>
                  </div>
                  <div>
                    <Button
                      type="link"
                      className="text-teal"
                      onClick={() => {
                        window.location.href = `/portal/calendar?groupEventId=${event.id}`
                      }}>
                      View
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Flyout visible={visible} closeFlyout={closeFlyout} header={"Scheduled Events"}>
      {renderContent()}
    </Flyout>
  )
}

export default ScheduledEventsFlyout
